import { FC, ReactNode } from 'react';
import { Box, BoxProps } from '@mui/material';
import { SettingsSectionHeader } from './settings-section-header';
import { WORKSPACE_CONTENT_PADDING } from './settings-workspace.styled';

type SettingsDataSectionProps = {
    children: ReactNode;
    /**
     * Optional section heading rendered inside the data section. It keeps
     * the workspace horizontal padding so the heading stays aligned with
     * the padded content while the data below bleeds to the pane edges.
     */
    title?: string;
    /**
     * Removes the workspace content padding so tabular data can use the
     * full content-pane width. Full bleed is bounded by the workspace
     * surface — it never crosses the local navigation or the workspace
     * border radius (overflow: hidden on the workspace clips it).
     */
    fullBleed?: boolean;
} & Omit<BoxProps, 'children' | 'title'>;

const SettingsDataSection: FC<SettingsDataSectionProps> = ({
    children,
    title,
    fullBleed = true,
    ...boxProps
}) => (
    <Box
        {...boxProps}
        sx={[
            (theme) => ({
                minWidth: 0,

                // Symmetric logical negative margins plus a matching calc()
                // width — equal geometry in RTL and LTR. A definite plain
                // width must not be set: in the column-flex content pane it
                // would disable cross-axis stretch and anchor the section to
                // one side, turning the opposite negative margin into a gap.
                ...(fullBleed && {
                    mx: `calc(-1 * ${theme.spacing(WORKSPACE_CONTENT_PADDING.inline)})`,
                    width: `calc(100% + ${theme.spacing(WORKSPACE_CONTENT_PADDING.inline * 2)})`,

                    [theme.breakpoints.up('md')]: {
                        mx: `calc(-1 * ${theme.spacing(WORKSPACE_CONTENT_PADDING.inlineMd)})`,
                        width: `calc(100% + ${theme.spacing(WORKSPACE_CONTENT_PADDING.inlineMd * 2)})`,
                    },
                }),
            }),
            ...(Array.isArray(boxProps.sx)
                ? boxProps.sx
                : boxProps.sx
                  ? [boxProps.sx]
                  : []),
        ]}
    >
        {title && (
            <SettingsSectionHeader
                title={title}
                sx={{
                    px: {
                        xs: WORKSPACE_CONTENT_PADDING.inline,
                        md: WORKSPACE_CONTENT_PADDING.inlineMd,
                    },
                }}
            />
        )}

        {children}
    </Box>
);

export { SettingsDataSection };
